import { type MutableRefObject, type RefObject, useCallback, useEffect, useRef } from 'react';
import type { ItemBounds } from './types';
import { getItemBoundsInTimeline } from './utils/coordinateMapping';

export interface CentralizedPositionManager {
  registerElement: (id: number, element: HTMLElement) => void;
  unregisterElement: (id: number) => void;
}

export function useCentralizedPositionManager(
  timelineContainerRef: RefObject<HTMLDivElement | null>,
  positionsRef: MutableRefObject<Map<number, ItemBounds>>,
  onPositionUpdate: (id: number, bounds: ItemBounds) => void,
): CentralizedPositionManager {
  const trackedElementsRef = useRef<Map<number, HTMLElement>>(new Map());
  const rafIdRef = useRef<number | undefined>(undefined);
  const isUpdateScheduledRef = useRef(false);

  const updatePositions = useCallback(() => {
    const container = timelineContainerRef.current;
    if (!container) return;

    const updates: Array<[number, ItemBounds]> = [];

    for (const [itemId, element] of trackedElementsRef.current.entries()) {
      try {
        const bounds = getItemBoundsInTimeline(element, container);
        const prev = positionsRef.current.get(itemId);
        const pixelStep = 2;

        if (
          !prev ||
          Math.abs(bounds.x - prev.x) > pixelStep ||
          Math.abs(bounds.y - prev.y) > pixelStep ||
          Math.abs(bounds.width - prev.width) > pixelStep ||
          Math.abs(bounds.height - prev.height) > pixelStep
        ) {
          updates.push([itemId, bounds]);
        }
      } catch {
        // Element may have been removed from DOM
      }
    }

    for (const [itemId, bounds] of updates) {
      onPositionUpdate(itemId, bounds);
    }

    isUpdateScheduledRef.current = false;
  }, [timelineContainerRef, positionsRef, onPositionUpdate]);

  const scheduleUpdate = useCallback(() => {
    if (isUpdateScheduledRef.current) return;

    isUpdateScheduledRef.current = true;
    rafIdRef.current = requestAnimationFrame(() => {
      updatePositions();
      rafIdRef.current = undefined;
    });
  }, [updatePositions]);

  useEffect(() => {
    const container = timelineContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', scheduleUpdate, { passive: true });

    return () => {
      container.removeEventListener('scroll', scheduleUpdate);
      if (rafIdRef.current !== undefined) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [timelineContainerRef, scheduleUpdate]);

  useEffect(() => {
    const container = timelineContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [timelineContainerRef, scheduleUpdate]);

  const registerElement = useCallback(
    (id: number, element: HTMLElement) => {
      trackedElementsRef.current.set(id, element);

      const container = timelineContainerRef.current;
      if (container) {
        try {
          const bounds = getItemBoundsInTimeline(element, container);
          onPositionUpdate(id, bounds);
        } catch {
          // Element may not be in DOM yet
        }
      }
    },
    [timelineContainerRef, onPositionUpdate],
  );

  const unregisterElement = useCallback((id: number) => {
    trackedElementsRef.current.delete(id);
  }, []);

  return {
    registerElement,
    unregisterElement,
  };
}
